import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import { Paper, ButtonGroup, Button } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { frFR, enUS } from '@mui/x-data-grid/locales';
import { formatDateTime, showDeleteResults } from '@utils/helpers';
import API from '@/utils/axios';
import theme from '@/theme';
import { useAuth } from '@contexts/AuthContext';
import { useSnackbar } from '@contexts/SnackbarContext';
import { useDialog } from '@contexts/DialogContext';
import useLocalStorage from '@/hooks/useLocalStorage';
import OurPagination from '@components/OurPagination';
import OurDataToolbar from '@components/OurDataToolbar';
import CustomerSearch from '@components/customer/CustomerSearch';

function CustomerTable() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { doLogout } = useAuth();
    const { showMessage } = useSnackbar();
    const { requestConfirm } = useDialog();
    const [loading, setLoading] = useState(true);
    const [customers, setCustomers] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [paginationModel, setPaginationModel] = useLocalStorage('customers.pagination', {
        pageSize: 10,
        page: 0,
    });
    const [sortModel, setSortModel] = useLocalStorage('customers.sort', [{
        field: 'id',
        sort: 'desc',
    }]);
    const [columnVisibility, setColumnVisibility] = useLocalStorage('customers.columns', {
        url: false,
        created_at: false,
        updated_at: false,
    });
    const [density, setDensity] = useLocalStorage('customers.density', 'standard');
    const [searchVisible, setSearchVisible] = useLocalStorage('customers.searchVisible', false);
    const [filter, setFilter] = useLocalStorage('customers.searchValues', {});
    const [selectedRows, setSelectedRows] = useState([]);

    const openEditPage = (params) => {
        navigate(`/customers/${params.id}`);
    };

    const toggleSearch = () => {
        if (searchVisible) {
            setFilter({});
        }
        setSearchVisible(!searchVisible);
    };

    const handleDelete = async () => {
        requestConfirm({
            message: t('Do you really want to delete the selected customers?'), 
            onConfirm: async () => {
                try {
                    const results = await API.delete(`/customers/batch`, { data: { ids: selectedRows } });
                    showDeleteResults(results, showMessage, t, '{{nb}} customer deleted successfully', '{{nb}} customers deleted successfully');
                    loadCustomers(paginationModel, sortModel); // Refresh
                } catch (error) {
                    if (error.response && error.response.status === 401) {
                        doLogout();
                    } else {
                        showMessage(t(error.message), 'error');
                    }
                }
            }
        });
    };

    const loadCustomers = useCallback(async (paginationModel, sortModel) => {
        try {
            setLoading(true);
            const { field = '', sort = '' } = sortModel[0] || {};
            let params = {
                per_page: paginationModel.pageSize,
                page: paginationModel.page + 1,
                sort: field,
                direction: sort,
            };
            if (filter.name) {
                filter.name = `%${filter.name}%`;
            }
            const response = await API.get('/customers', { params: { ...params, ...filter } });
            setCustomers(
                response.data.data.map((customer) => ({
                    id: customer.id,
                    name: customer.name,
                    active: customer.active,
                    url: customer.url,
                    credits: customer.credits,
                    created_at: new Date(customer.created_at),
                    updated_at: new Date(customer.updated_at),
                }))
            );
            setRowCount(response.data.total);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            if (error.response && error.response.status === 401) {
                doLogout();
            } else {
                console.error('Erreur de chargement', error);
            }
        }
    }, [doLogout, filter]);

    useEffect(() => {
        loadCustomers(paginationModel, sortModel);
    }, [loadCustomers, paginationModel, sortModel]);

    const localeTheme = i18n.language === 'fr' ? frFR : enUS;
    const themeWithLocale = createTheme(theme, localeTheme);

    const columns = [
        {
            field: 'id',
            headerName: t('ID'),
            flex: 0.5,
            type: 'number',
            maxWidth: 70,
        },
        {
            field: 'name',
            headerName: t('Name'),
            flex: 1,
            type: 'string'
        },
        {
            field: 'active',
            headerName: t('Active|customer'),
            flex: 0.5,
            type: 'boolean',
        },
        {
            field: 'url',
            headerName: t('Site URL'),
            flex: 1,
            type: 'string',
        },
        {
            field: 'sms_remaining_credits',
            headerName: t('Remaining {{type}} credits', { type: t('credit.sms') }),
            flex: 0.5,
            type: 'number',
            valueGetter: (value, row) => row.credits?.sms ?? 0
        },
        {
            field: 'email_remaining_credits',
            headerName: t('Remaining {{type}} credits', { type: t('credit.email') }),
            flex: 0.5,
            type: 'number',
            valueGetter: (value, row) => row.credits?.email ?? 0
        },
        {
            field: 'created_at',
            headerName: t('Creation date'),
            flex: 1,
            type: 'dateTime',
            valueFormatter: formatDateTime
        },
        {
            field: 'updated_at',
            headerName: t('Update date'),
            flex: 1,
            type: 'dateTime',
            valueFormatter: formatDateTime
        }
    ];

    return (
        <ThemeProvider theme={themeWithLocale}>
            <Paper>
                {searchVisible && <CustomerSearch handleSubmit={setFilter} filter={filter} />}
                <DataGrid
                    autoHeight
                    loading={loading}
                    slots={{
                        toolbar: OurDataToolbar,
                        pagination: OurPagination,
                    }}
                    slotProps={{
                        toolbar: { searchVisible, toggleSearch },
                    }}
                    initialState={{
                        sorting: { sortModel }
                    }}
                    rows={customers}
                    columns={columns}
                    pageSizeOptions={[10, 15, 20, 50, 100]}
                    pagination
                    paginationMode="server"
                    rowCount={rowCount}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    columnVisibilityModel={columnVisibility}
                    onColumnVisibilityModelChange={(newModel) =>
                        setColumnVisibility(newModel)
                    }
                    sortingMode="server"
                    sortingOrder={['asc', 'desc']}
                    onSortModelChange={setSortModel}
                    density={density}
                    onDensityChange={setDensity}
                    disableRowSelectionOnClick
                    disableSelectionOnClick
                    disableColumnResize={true}
                    disableColumnFilter
                    checkboxSelection
                    onRowClick={openEditPage}
                    onRowSelectionModelChange={(newSelectionModel) => {
                        setSelectedRows(newSelectionModel);
                    }}
                />

            </Paper>

            <ButtonGroup variant="contained" aria-label="Actions" sx={{ mt: 2 }}>
                <Button onClick={handleDelete} startIcon={<DeleteIcon />}
                    disabled={selectedRows.length === 0}>
                    {t('Delete')}
                </Button>
            </ButtonGroup>
        </ThemeProvider>
    );
}

export default CustomerTable;
